<template>
  <!-- A headerless rounded dialog holding a search row, a listbox of options moved through with the
       arrow keys, and a footer of key hints. The command center and the bulk actions palette both use it. -->
  <TwnDialog
    v-model:visible="visible"
    :closable="false"
    :header="label"
    :modal="true"
    :pt="DIALOG_PT"
  >
    <div
      v-focustrap
      class="flex flex-col"
      @keydown="onKeydown"
    >
      <TwnPaletteSearch
        ref="search"
        v-model="query"
        :input-attrs="searchAttrs"
        :placeholder="placeholder"
        @enter="onEnter"
      >
        <template
          v-if="$slots['search-leading']"
          #leading
        >
          <slot name="search-leading" />
        </template>
        <template #trailing>
          <slot name="search-trailing" />
        </template>
      </TwnPaletteSearch>

      <!-- Between the search and the rows: what narrows the search, not what results from it. The
           command center puts its scope pills here. Left empty it takes no space at all. -->
      <slot name="filters" />

      <!-- The height caps at 60% of the screen, or 26rem on a tall one, so the search row never leaves view. -->
      <div
        :id="listId"
        ref="list"
        class="max-h-[min(60vh,26rem)] overflow-auto p-2"
        role="listbox"
      >
        <!-- While loading, the rows are held back: a cached list may be stale, and a stale row must not be picked. -->
        <div
          v-if="loading"
          aria-busy="true"
          aria-label="Loading"
          class="flex justify-center py-8"
          role="status"
        >
          <TwnLoadingSpinner size="small" />
        </div>

        <div
          v-for="section in visibleSections"
          :key="section.key"
          :aria-label="section.label"
          class="pt-2 first:pt-0"
          role="group"
        >
          <p
            v-if="section.label"
            aria-hidden="true"
            class="px-3 pb-1 pt-1 text-sm font-semibold text-surface-500"
          >
            {{ section.label }}
          </p>

          <template
            v-for="row in section.rows"
            :key="row.key"
          >
            <div
              v-if="row.isStatus"
              aria-disabled="true"
              class="px-3 py-2 text-center text-sm text-surface-400"
              role="option"
            >
              {{ row.label }}
            </div>
            <TwnPaletteOption
              v-else
              :id="optionId(row)"
              :active="isActive(row)"
              :badge-class="row.badgeClass"
              :danger="row.danger"
              :disabled="row.disabled"
              :icon="row.icon"
              :label="row.label"
              :option-key="row.key"
              :sub-label="row.subLabel"
              @hover="setActive(row.key)"
              @select="emit('select', row)"
            >
              <template #trailing>
                <slot
                  :row="row"
                  name="option-trailing"
                />
              </template>
            </TwnPaletteOption>
          </template>
        </div>

        <p
          v-if="!loading && !hasRows"
          aria-disabled="true"
          class="px-3 py-8 text-center text-sm text-surface-400"
          role="option"
        >
          {{ emptyMessage }}
        </p>
      </div>

      <TwnPaletteKeyHints :hints="resolvedHints" />
    </div>
  </TwnDialog>
</template>

<script lang="ts" setup generic="TData">
import { computed, nextTick, ref, useId, useTemplateRef, watch } from 'vue'

import type {
  TwnPaletteDialogEmits,
  TwnPaletteDialogProps,
  TwnPaletteDialogRowData,
  TwnPaletteDialogSectionData
} from '@/components/organisms/twn-palette-dialog/TwnPaletteDialog.types'

import TwnDialog from '@/components/atoms/prime/dialog/twn-dialog/TwnDialog.vue'
import TwnLoadingSpinner from '@/components/atoms/progress/twn-loading-spinner/TwnLoadingSpinner.vue'
import TwnPaletteKeyHints from '@/components/molecules/palette/twn-palette-key-hints/TwnPaletteKeyHints.vue'
import TwnPaletteOption from '@/components/molecules/palette/twn-palette-option/TwnPaletteOption.vue'
import TwnPaletteSearch from '@/components/molecules/palette/twn-palette-search/TwnPaletteSearch.vue'
import { PALETTE_DEFAULT_HINTS } from '@/components/organisms/twn-palette-dialog/TwnPaletteDialog.consts'

const props = withDefaults(defineProps<TwnPaletteDialogProps<TData>>(), {
  placeholder: 'Search…',
  loading: false,
  emptyMessage: 'No matches',
  filterLocally: false,
  selectOnlyMatch: true,
  canGoBack: false,
  blockEnter: false,
  hints: undefined,
  inputAttrs: undefined
})

const emit = defineEmits<TwnPaletteDialogEmits<TData>>()

const visible = defineModel<boolean>('visible', { default: false })
// The caller owns the query, so a scanned barcode set as the palette opens is never wiped.
const query = defineModel<string | undefined>('query', { default: undefined })

// The width is the screen less a gutter, up to 640px: a palette reads as one column, never a page.
const DIALOG_PT = {
  root: { class: 'w-[92vw] max-w-[640px] !rounded-2xl' },
  header: { class: '!hidden' },
  content: { class: '!p-0 !rounded-2xl overflow-hidden' }
}

const listId = useId()

const search = useTemplateRef<InstanceType<typeof TwnPaletteSearch>>('search')
const list = useTemplateRef<HTMLDivElement>('list')

// No row is highlighted until the user arrow-keys or hovers.
const activeKey = ref<string | undefined>()

const visibleSections = computed<TwnPaletteDialogSectionData<TData>[]>(() => {
  if (props.loading) return []

  const needle = query.value?.trim().toLowerCase()
  const isShown = (row: TwnPaletteDialogRowData<TData>): boolean =>
    !props.filterLocally || !needle || row.isStatus === true || row.label.toLowerCase().includes(needle)

  return props.sections
    .map(section => ({ ...section, rows: section.rows.filter(isShown) }))
    .filter(section => section.rows.length > 0)
})

const selectableRows = computed(() =>
  visibleSections.value.flatMap(section => section.rows).filter(row => !row.isStatus && !row.disabled)
)

// A row key may hold any character, so an option's DOM id is its position instead.
const optionIds = computed(
  () => new Map(selectableRows.value.map((row, index) => [row.key, `${listId}-option-${index}`]))
)

const hasRows = computed(() => visibleSections.value.length > 0)

const resolvedHints = computed(() => props.hints ?? PALETTE_DEFAULT_HINTS)

// The search box drives the listbox, so a screen reader announces the highlighted row as it moves.
const searchAttrs = computed(() => {
  const activeId = activeKey.value ? optionIds.value.get(activeKey.value) : undefined

  return {
    ...props.inputAttrs,
    role: 'combobox',
    'aria-controls': listId,
    'aria-expanded': 'true',
    ...(activeId ? { 'aria-activedescendant': activeId } : {})
  }
})

const optionId = (row: TwnPaletteDialogRowData<TData>): string | undefined => optionIds.value.get(row.key)

const isActive = (row: TwnPaletteDialogRowData<TData>): boolean => row.key === activeKey.value

const setActive = (key: string): void => {
  activeKey.value = key
}

const clearActive = (): void => {
  activeKey.value = undefined
}

// Nothing highlighted yet: ArrowDown lands on the first row, ArrowUp on the last. Both wrap.
const moveActive = (delta: number): void => {
  const keys = selectableRows.value.map(row => row.key)
  if (!keys.length) return

  const currentIndex = keys.indexOf(activeKey.value ?? '')
  const nextIndex =
    currentIndex === -1 ? (delta > 0 ? 0 : keys.length - 1) : (currentIndex + delta + keys.length) % keys.length
  setActive(keys[nextIndex])
}

const onKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault()
    moveActive(event.key === 'ArrowDown' ? 1 : -1)
    return
  }

  if (event.key === 'Backspace' && props.canGoBack && !query.value) {
    event.preventDefault()
    emit('back')
  }
}

// Enter is the palette's own, since what it does with no highlight differs per caller: a highlighted row
// always wins; with none, the only row left, when the caller allows it.
const onEnter = (value: string | undefined): void => {
  if (props.blockEnter) return

  const highlighted = selectableRows.value.find(isActive)
  if (highlighted) {
    emit('select', highlighted)
    return
  }

  if (props.selectOnlyMatch && selectableRows.value.length === 1) {
    emit('select', selectableRows.value[0])
    return
  }

  emit('enter', value)
}

// A stale key must never point at a different row once the rows change.
watch(optionIds, ids => {
  if (activeKey.value && !ids.has(activeKey.value)) clearActive()
})

watch(activeKey, async key => {
  if (!key) return
  await nextTick()
  list.value?.querySelector(`[data-key="${CSS.escape(key)}"]`)?.scrollIntoView({ block: 'nearest' })
})

watch(visible, async isVisible => {
  clearActive()
  if (!isVisible) return

  await nextTick()
  search.value?.focus()
})

defineExpose({
  focus: () => search.value?.focus(),
  clearActive
})
</script>
